// Bridges plugin-contributed tools (manifest `contributes.tools`) into the
// bottom Tools panel. Each declared tool becomes a ToolDefinition whose body is
// the plugin's same-id `type:'panel'` surface, rendered through the sandboxed
// SurfaceHost. A reconciler diffs `host.listTools()` against the ToolRegistry so
// install/enable/disable add or remove the tool; ToolPanel re-derives via
// ToolRegistry.subscribe, so the tab appears automatically.

import type { ReactElement } from 'react';
import { SurfaceHost } from '../plugins/surfaces/SurfaceHost';
import { desktopSurfaceSrc } from '../plugins/surfaces/surfaceSrc';
import type { PluginSystem } from '../plugins/setup';
import type { DeclaredTool } from '../plugins/PluginHost';
import { ToolRegistry } from './ToolRegistry';
import type { ToolDefinition, ToolModule, ToolViewProps } from './types';

export function createPluginToolDefinition(
  system: PluginSystem,
  declared: DeclaredTool,
): ToolDefinition {
  const { pluginId, id, title, icon } = declared;
  return {
    id,
    title,
    icon,
    instances: 'single',
    async load(): Promise<ToolModule> {
      // Convention: a tool `X` is backed by the surface whose id is `X` and
      // type is 'panel'. Resolve it once at load; if absent, render a hint.
      const surface = system.host
        .listSurfaces()
        .find((s) => s.pluginId === pluginId && s.id === id && s.type === 'panel');
      const component = (_props: ToolViewProps): ReactElement => {
        if (!surface) {
          return <div className="mp-tool-empty">This tool has no panel surface.</div>;
        }
        return (
          <SurfaceHost
            system={system}
            pluginId={pluginId}
            surfaceId={id}
            src={desktopSurfaceSrc(pluginId, surface.path)}
            title={title}
            className="mp-plugin-tool-surface"
          />
        );
      };
      return { component };
    },
  };
}

export function registerPluginContributedTools(system: PluginSystem): () => void {
  const host = system.host;
  const registered = new Map<string, () => void>();

  const sync = (): void => {
    const desired = new Map(host.listTools().map((t) => [t.id, t]));

    // Remove tools whose contribution is gone (uninstall/disable).
    for (const [id, unregister] of [...registered]) {
      if (!desired.has(id)) {
        unregister();
        registered.delete(id);
      }
    }

    // Add newly-visible tools. Guard against id collisions with built-in tools
    // (e.g. 'terminal') so a duplicate-id register cannot throw.
    for (const [id, tool] of desired) {
      if (registered.has(id) || ToolRegistry.getById(id)) continue;
      registered.set(id, ToolRegistry.register(createPluginToolDefinition(system, tool)));
    }
  };

  sync();
  const unsub = host.onDidChangeContributions(sync);
  return () => {
    unsub();
    for (const unregister of registered.values()) unregister();
    registered.clear();
  };
}
